import React from "react"
import {Row, Col, Switch, Checkbox} from "antd"
import {cloneDeep, isEqual, keyBy} from "lodash"
import {BasePopup} from "components/popup"
import {BaseInput, BasePhoneInput, BaseTextArea} from "components/inputs"
import {userPersonalContactRolesServiceV3, userPersonalContactsServiceV3} from "services"
import {KlassDropdown} from "components/Select"
import {BaseButton} from "components/buttons"
import UserSelectV3 from "components/UserSelectV3"
import styles from "./AddContactForm.module.css"
import {withTranslation} from "react-i18next"
import {validateEmail} from "helpers/validation"
import {getValuesChangeTwoObj, generateUniqueEmail, handleError, toastError, toastSuccess} from "helpers"
import {FormLabel} from "components/Form"
import {Auth} from "types/auth"
import {useModel} from "hooks"

type Props = {
    isShow: boolean
    onClose: Function
    data?: any
    userId?: number
    t: Function
    selectRelatedStudent?: boolean
    slug?: string
}

type State = {
    errors: any
    newData: any
    oldData: any
    isSubmitting: boolean
    showDefault: boolean
    relatedStudent: any
    personalContactRoles: Array<{id: number; name: string}>
    noEmail: boolean
}

const disableDefaultByRole = ["Other", "Family"]
const DEFAULT_DATA = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    message: "",
    userPersonalContactRoleId: null,
    isEmergency: false,
    isDefault: false
}

const buildFormData = (data) => {
    if (!data) {
        return cloneDeep(DEFAULT_DATA)
    }
    const {contactProfile, isDefault, isEmergency, userPersonalContactRoleId, message, id, phone} = data
    if (id) {
        return cloneDeep({...contactProfile, isDefault, isEmergency, userPersonalContactRoleId, id, message})
    }
    return cloneDeep({...DEFAULT_DATA, phone: phone || ""})
}

class AddContactForm extends React.Component<Props, State> {
    mounted = false

    constructor(props) {
        super(props)
        const initialData = buildFormData(props.data)
        this.state = {
            newData: initialData,
            isSubmitting: false,
            errors: {},
            showDefault: true,
            oldData: cloneDeep(initialData),
            relatedStudent: null,
            personalContactRoles: [],
            noEmail: false
        }
    }

    componentDidMount() {
        this.mounted = true
        this.loadPersonalContactRoles()
    }

    componentWillUnmount() {
        this.mounted = false
    }

    loadPersonalContactRoles = async () => {
        try {
            const {data} = await userPersonalContactRolesServiceV3.getAll()
            if (!this.mounted) {
                return
            }
            this.setState({
                personalContactRoles: (data || []).map((personalRole) => ({
                    id: personalRole.userPersonalContactRoleId,
                    name: personalRole.name
                }))
            })
        } catch (error) {
            if (this.mounted) {
                handleError(error)
            }
        }
    }

    componentDidUpdate(prevProps) {
        const {data} = this.props
        if (!isEqual(prevProps.data, data)) {
            const nextData = buildFormData(data)
            this.setState({
                newData: nextData,
                oldData: cloneDeep(nextData),
                relatedStudent: null,
                errors: {},
                noEmail: false
            })
        }
    }

    onChangeData = (key, value) => {
        const {newData, errors, personalContactRoles} = this.state
        let showDefaultByRole = true
        delete errors[key]
        if ("userPersonalContactRoleId" === key) {
            const roleName = personalContactRoles.find((role) => role.id === value)
            showDefaultByRole = !disableDefaultByRole.includes(roleName?.name)
        }
        newData[key] = value
        this.setState({newData, showDefault: showDefaultByRole})
    }

    onToggleNoEmail = (checked: boolean) => {
        const {newData, errors} = this.state
        if (checked) {
            newData.email = ""
            delete errors.email
        }
        this.setState({noEmail: checked, newData, errors})
    }

    validateForm = () => {
        const {newData, errors, noEmail} = this.state
        const {t} = this.props
        const newErrors = {...errors}
        const ids = Object.keys(newData)
        const optionalFields = ["isEmergency", "isDefault", "address", "phone", "message"]
        if (noEmail) {
            optionalFields.push("email")
        }
        ids.forEach((id) => {
            if (!newData[id] && !optionalFields.includes(id)) {
                newErrors[id] = "Field is required"
            } else {
                delete newErrors[id]
            }
        })
        if (this.props.selectRelatedStudent && !this.state.relatedStudent?.id) {
            newErrors.relatedStudent = "Field is required"
        } else {
            delete newErrors.relatedStudent
        }
        if (!noEmail && newData.email && !validateEmail(newData.email)) {
            toastError(t("user:contact.validEmail"))
            return false
        }
        if (Object.keys(newErrors).length) {
            toastError(t("common:message.completeForm"))
            this.setState({errors: newErrors})
            return false
        }
        return true
    }

    onSubmit = async () => {
        const {newData, relatedStudent, noEmail} = this.state
        const {userId, t, selectRelatedStudent, slug} = this.props
        const isInvalid = this.validateForm()
        if (!isInvalid) {
            return
        }
        this.setState({isSubmitting: true})
        try {
            const sendData = {
                ...newData,
                email: noEmail ? generateUniqueEmail(`${slug}.edular`) : newData.email,
                userId: selectRelatedStudent ? relatedStudent.id : userId,
                isEmergency: newData.isEmergency ? 1 : 0,
                isDefault: newData.isDefault ? 1 : 0
            }
            await userPersonalContactsServiceV3.invite(sendData)
            this.props.onClose()
            const resetData = buildFormData(this.props.data)
            this.setState({
                newData: resetData,
                oldData: cloneDeep(resetData),
                relatedStudent: null,
                noEmail: false
            })
            toastSuccess(t("user:contact.addContactSuccess"))
        } catch (e) {
            handleError(e)
        } finally {
            this.setState({isSubmitting: false})
        }
    }
    getDataSubmit = () => {
        const {oldData, newData} = this.state
        const submitData: any = getValuesChangeTwoObj(oldData, newData)
        if (typeof submitData?.isEmergency === "boolean") {
            submitData.isEmergency = submitData.isEmergency ? 1 : 0
        }
        if (typeof submitData?.isDefault === "boolean") {
            submitData.isDefault = submitData.isDefault ? 1 : 0
        }
        return submitData
    }

    onEdit = async () => {
        const {data, t} = this.props
        const isInvalid = this.validateForm()
        if (!isInvalid) {
            return
        }
        try {
            this.setState({isSubmitting: true})
            const submitData = this.getDataSubmit()
            await userPersonalContactsServiceV3.update(data.id, submitData)
            toastSuccess(t("common:message.updateSuccess"))
            this.props.onClose()
        } catch (e) {
            toastError(t("common:message.updateFailure"))
        } finally {
            this.setState({
                newData: cloneDeep(DEFAULT_DATA),
                oldData: cloneDeep(DEFAULT_DATA)
            })
            this.setState({isSubmitting: false})
        }
    }

    onClickCancelBtn = () => {}

    render() {
        const {newData, isSubmitting, errors, showDefault, relatedStudent, personalContactRoles, noEmail} = this.state
        const {isShow, onClose, t, selectRelatedStudent} = this.props
        const roles = personalContactRoles
        const rolesKeyById = keyBy(roles, "id")
        const roleValue = rolesKeyById[newData.userPersonalContactRoleId] || null
        const isChanged = this.getDataSubmit()

        return (
            <BasePopup isShow={isShow} onClose={onClose} leftIcon="PERSON_FILL" leftIconColor="#fff" width="880px">
                <p className={styles.title}>{newData.id ? t("contact.detail") : t("user.contact")}</p>
                <div className={styles.bodyWrap}>
                    {selectRelatedStudent && (
                        <div className={styles.bodyItem}>
                            <FormLabel label={t("user.student")} isRequired />
                            <UserSelectV3
                                type={[Auth.UserProfileType.Student]}
                                value={relatedStudent}
                                onChange={(student) => {
                                    const nextErrors = {...this.state.errors}
                                    delete nextErrors.relatedStudent
                                    this.setState({relatedStudent: student, errors: nextErrors})
                                }}
                                placeholder={t("user.student")}
                                className={!!errors["relatedStudent"] ? styles.errorSelect : ""}
                                menuPortalTarget={null}
                            />
                        </div>
                    )}
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className={styles.bodyItem}>
                                <FormLabel label={t("user.firstName")} isRequired />
                                <BaseInput
                                    onChange={(newValue) => this.onChangeData("firstName", newValue)}
                                    placeholder={t("user.firstName")}
                                    value={newData.firstName}
                                    className={!!errors["firstName"] ? styles.error : ""}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className={styles.bodyItem}>
                                <FormLabel label={t("user.lastName")} isRequired />
                                <BaseInput
                                    onChange={(newValue) => this.onChangeData("lastName", newValue)}
                                    placeholder={t("user.lastName")}
                                    value={newData.lastName}
                                    className={!!errors["lastName"] ? styles.error : ""}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className={styles.bodyItem}>
                        <div className={styles.emailLabelWrap}>
                            <FormLabel label={t("user.email")} isRequired={!noEmail} />
                            {!newData.id && (
                                <Checkbox
                                    checked={noEmail}
                                    onChange={(e) => this.onToggleNoEmail(e.target.checked)}
                                    className={styles.noEmail}>
                                    No Email
                                </Checkbox>
                            )}
                        </div>
                        <BaseInput
                            onChange={(newValue) => this.onChangeData("email", newValue)}
                            placeholder={t("user.email")}
                            value={newData.email}
                            disabled={noEmail}
                            className={!!errors["email"] ? styles.error : ""}
                        />
                    </div>
                    <div className={styles.bodyItem}>
                        <p className={styles.bodyItemTitle}>{t("user.address")}</p>
                        <BaseInput
                            onChange={(newValue) => this.onChangeData("address", newValue)}
                            placeholder={t("user.address")}
                            value={newData.address}
                            className={!!errors["address"] ? styles.error : ""}
                        />
                    </div>
                    <div className={styles.bodyItem}>
                        <p className={styles.bodyItemTitle}>{t("user.phone")}</p>
                        <BasePhoneInput
                            onChange={(newValue) => this.onChangeData("phone", newValue)}
                            value={newData.phone}
                            isError={!!errors["phone"]}
                        />
                    </div>
                    <div className={styles.bodyItem}>
                        <p className={styles.bodyItemTitle}>{t("user.notes")}</p>
                        <BaseTextArea
                            onChange={(newValue) => this.onChangeData("message", newValue)}
                            placeholder={t("user.notes")}
                            value={newData.message}
                            className={!!errors["message"] ? styles.error : ""}
                        />
                    </div>
                </div>
                <Row>
                    <Col span={12}>
                        <div>
                            <FormLabel
                                label={t("user:contact.relationship")}
                                className={styles.bodyItemTitle}
                                isRequired
                            />
                            <Checkbox
                                checked={newData.isDefault}
                                onChange={({target: {checked}}) => this.onChangeData("isDefault", checked)}
                                className={styles.checkboxItemRight}
                                disabled={!showDefault}>
                                {t("user:contact.makeDefault")}
                            </Checkbox>
                            <div>
                                <KlassDropdown
                                    onChange={(newValue) => this.onChangeData("userPersonalContactRoleId", newValue.id)}
                                    options={roles}
                                    placeholder={t("user.role")}
                                    value={roleValue}
                                    className={!!errors["userPersonalContactRoleId"] ? styles.errorSelect : ""}
                                    menuPortalTarget={null}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={styles.emergencyWrap}>
                            <p className={styles.bodyItemTitle}>{t("common:message.emergencyContact")}</p>
                            <div className={styles.emergencyBodyWrap}>
                                <p className={styles.emergencyText}>No</p>
                                <Switch
                                    checked={newData.isEmergency}
                                    onChange={(newValue) => this.onChangeData("isEmergency", newValue)}
                                    className={!!errors["isEmergency"] ? styles.error : ""}
                                />
                                <p className={styles.emergencyTextYes}>{t("common:message.yes")}</p>
                            </div>
                        </div>
                    </Col>
                </Row>
                <div className={styles.actionWrap}>
                    <div className={styles.addBtnWrap}>
                        <BaseButton
                            title={newData.id ? t("common:action.save") : t("common:action.add")}
                            onClick={newData.id ? this.onEdit : this.onSubmit}
                            className={styles.addBtn}
                            loading={isSubmitting}
                            disabled={!isChanged}
                        />
                    </div>
                </div>
            </BasePopup>
        )
    }
}

function AddContactFormContainer(props) {
    const model = useModel()
    return <AddContactForm {...props} slug={model.slug} />
}

export default withTranslation(["user", "chat"])(AddContactFormContainer)
